import type { Route } from "next";

import { authEntryHref } from "@/lib/auth/sign-up";
import { buildOrganizationPath } from "@/lib/organization-path";
import { resolveBillingInterval, resolvePlanCode } from "@/lib/pricing-display";

const PLAN_INTENT_QUERY_PLAN = "plan";
const PLAN_INTENT_QUERY_INTERVAL = "interval";
const PLAN_INTENT_STORAGE_KEY = "agroproj.pricingPlanIntent";

const PLAN_CODES = ["free", "per_project", "unlimited"] as const;
const INTERVALS = ["month", "year"] as const;

type PricingPlanCode = (typeof PLAN_CODES)[number];
type PricingInterval = (typeof INTERVALS)[number];

export type PlanIntent = {
  plan: PricingPlanCode;
  interval: PricingInterval;
};

function isPlanCode(value: string | undefined): value is PricingPlanCode {
  return PLAN_CODES.some((code) => code === value);
}

function isInterval(value: string | undefined): value is PricingInterval {
  return INTERVALS.some((interval) => interval === value);
}

function firstString(value: string | string[] | null | undefined): string | undefined {
  if (Array.isArray(value)) return value[0];
  return value ?? undefined;
}

export function parsePlanIntent(input: {
  plan?: string | string[] | null;
  interval?: string | string[] | null;
}): PlanIntent | null {
  const plan = firstString(input.plan);
  if (!isPlanCode(plan)) return null;

  const interval = firstString(input.interval);
  if (interval === undefined || interval === "") {
    return { plan, interval: "month" };
  }
  if (!isInterval(interval)) return null;
  return { plan, interval };
}

export function buildPlanIntentSearch(intent: PlanIntent): string {
  const params = new URLSearchParams({
    [PLAN_INTENT_QUERY_PLAN]: intent.plan,
    [PLAN_INTENT_QUERY_INTERVAL]: intent.interval,
  });
  return `?${params.toString()}`;
}

export function withPlanIntent(path: string, intent: PlanIntent | null): string {
  if (!intent) return path;

  const hashIndex = path.indexOf("#");
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : "";
  const withoutHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path;
  const queryIndex = withoutHash.indexOf("?");
  const pathname = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  return `${pathname}${buildPlanIntentSearch(intent)}${hash}`;
}

export function persistPlanIntent(intent: PlanIntent) {
  try {
    sessionStorage.setItem(PLAN_INTENT_STORAGE_KEY, JSON.stringify(intent));
  } catch {
    // Private browsing or disabled storage.
  }
}

export function readStoredPlanIntent(): PlanIntent | null {
  try {
    const raw = sessionStorage.getItem(PLAN_INTENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const record = parsed as { plan?: unknown; interval?: unknown };
    if (typeof record.plan !== "string" || typeof record.interval !== "string") return null;
    return parsePlanIntent({ plan: record.plan, interval: record.interval });
  } catch {
    return null;
  }
}

export function buildPricingActionHref({
  isSignedIn,
  intent,
}: {
  isSignedIn: boolean;
  intent: PlanIntent | null;
}): Route {
  const path = isSignedIn ? "/app" : authEntryHref;
  return withPlanIntent(path, intent) as Route;
}

export function appEntryHrefFromIntent(intent: PlanIntent | null): Route {
  return withPlanIntent("/app", intent) as Route;
}

export function matchPriceOptionForIntent<
  T extends {
    product: { metadata?: Record<string, string> };
    price: { metadata?: Record<string, string>; recurringInterval?: string };
  },
>(priceOptions: T[], intent: PlanIntent | null): T | null {
  if (!intent) return null;
  const byPlanAndInterval = priceOptions.find((option) => {
    const planCode = resolvePlanCode(option.price.metadata, option.product.metadata);
    const interval = resolveBillingInterval(option.price.metadata, option.price.recurringInterval);
    return planCode === intent.plan && interval === intent.interval;
  });
  if (byPlanAndInterval) return byPlanAndInterval;
  return (
    priceOptions.find((option) => {
      const planCode = resolvePlanCode(option.price.metadata, option.product.metadata);
      return planCode === intent.plan;
    }) ?? null
  );
}

export function workspacePathFromPlanIntent(slug: string, intent: PlanIntent | null): string {
  if (!intent || intent.plan === "free") {
    return buildOrganizationPath(slug, "/");
  }
  return buildOrganizationPath(slug, `/settings/billing${buildPlanIntentSearch(intent)}`);
}
